import { TFunction } from 'i18next';
import { produce } from 'immer';
import party from 'party-js';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  setValidationErrors,
  showErrorToast,
} from '../../../../../common/global/helpers';
import useIsMobile from '../../../../../common/hooks/useIsMobile';
import useMount from '../../../../../common/hooks/useMount';
import Button from '../../../../../common/widgets/Button';
import Checkbox from '../../../../../common/widgets/Checkbox';
import { ErrorPanel } from '../../../../../common/widgets/ErrorPanel';
import Input from '../../../../../common/widgets/Input';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { ContestStatuses, TopLevelCategories } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { ContestDetailsModel } from '../../../models/Contest';
import { ErrorState, ErrorType, ValidationError } from '../../../models/Errors';
import { resources } from '../../../resources/resources';
import { contestService } from '../../../services/contest';
import { visitedContestIdsKey } from '../../HomePage/Contest';

const openContestTerms = async (
  event: MouseEvent,
  contestId: number,
  translate: TFunction,
) => {
  event.preventDefault();

  const response = await contestService.openContestTerms(contestId);

  if (!response.hasErrors) {
    return;
  }

  if (response.errorCode === 'popup_blocked') {
    showErrorToast('Browserul a blocat deschiderea documentului.');
    return;
  }

  showErrorToast(translate(resources.errors.generic));
};

interface ContestForm {
  name: string;
  email: string;
  phoneNumber: string;
  acceptedTerms: boolean;
  acceptedPrivacyPolicy: boolean;
}

const confettiOptions = { count: 30, spread: 20 };

export const ContestDetailsPage = () => {
  const params = useParams();
  const [activeComponent, setActiveComponent] = useState<ReactNode | null>(
    null,
  );
  const [contest, setContest] = useState<ContestDetailsModel | null>(null);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const { isMobile } = useIsMobile();

  const loadContest = async (abortController?: AbortController) => {
    if (!params.contestId || Number.isNaN(Number.parseInt(params.contestId)))
      return;

    const resp = await contestService.getContestDetails(
      Number.parseInt(params.contestId),
      {
        signal: abortController?.signal,
      },
    );

    if (abortController?.signal.aborted) return;

    if (!resp.hasErrors) {
      setContest(resp.data);

      const visitedContestIds: number[] = JSON.parse(
        localStorage.getItem(visitedContestIdsKey) ?? '[]',
      );
      if (!visitedContestIds.includes(resp.data.contestId)) {
        const newContestIds = [...visitedContestIds, resp.data.contestId];
        localStorage.setItem(
          visitedContestIdsKey,
          JSON.stringify(newContestIds),
        );
      }

      if (resp.data.status === ContestStatuses.CustomerNotRegistered)
        setActiveComponent(
          <ContestCustomerNotRegistered
            contest={resp.data}
            onCustomerRegistered={() =>
              setActiveComponent(
                <ContestCustomerRegistered contest={resp.data} />,
              )
            }
          />,
        );
      if (resp.data.status === ContestStatuses.CustomerRegistered)
        setActiveComponent(<ContestCustomerRegistered contest={resp.data} />);
      if (resp.data.status === ContestStatuses.PendingForWinners)
        setActiveComponent(<ContestPendingForWinners contest={resp.data} />);
      if (resp.data.status === ContestStatuses.WinnersSelected)
        setActiveComponent(<ContestWinners contest={resp.data} />);
    } else {
      setShowErrorPanel(true);
    }
  };

  useMount(loadContest);

  if (showErrorPanel)
    return (
      <section className="section-main section-full contest-error">
        <div className="container">
          <ErrorPanel />
        </div>
      </section>
    );

  return (
    <Loader loading={!contest} loader={<LoadingSpinner />}>
      {!!contest && (
        <>
          {isMobile && (
            <div className="contest-image">
              <img
                src={rewriteImageUrl(contest.bannerMobile)}
                alt={contest.title}
              />
            </div>
          )}
          <section className="section-main section-full contest-page">
            <div className="container">
              {!isMobile && (
                <div className="contest-image">
                  <img
                    src={rewriteImageUrl(contest.bannerDesktop)}
                    alt={contest.title}
                  />
                </div>
              )}

              <div className="contest-content">{activeComponent}</div>
            </div>
          </section>
        </>
      )}
    </Loader>
  );
};

const ContestCustomerNotRegistered = ({
  contest,
  onCustomerRegistered,
}: {
  contest: ContestDetailsModel;
  onCustomerRegistered: () => void;
}) => {
  const [isSaveButtonLoading, setIsSaveButtonLoading] =
    useState<boolean>(false);
  const [form, setForm] = useState<ContestForm>({
    name: '',
    email: '',
    phoneNumber: '',
    acceptedTerms: false,
    acceptedPrivacyPolicy: false,
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const { t: translate } = useTranslation();
  const isSavingRef = useRef(false);

  const onFormChange = (field: keyof ContestForm, value: any) => {
    setForm(
      produce(d => {
        (d[field] as any) = value;
      }),
    );
  };

  const validateForm = (): boolean => {
    setErrors(
      produce(d => {
        if (!form.name) d.name = [getRequiredValidationError('name')];
        if (!form.email) d.email = [getRequiredValidationError('email')];
        if (!form.phoneNumber)
          d.phoneNumber = [getRequiredValidationError('phoneNumber')];
        if (!form.acceptedTerms)
          d.acceptedTerms = [getRequiredValidationError('acceptedTerms')];
        if (!form.acceptedPrivacyPolicy)
          d.acceptedPrivacyPolicy = [
            getRequiredValidationError('acceptedPrivacyPolicy'),
          ];
      }),
    );
    return (
      !form.name ||
      !form.email ||
      !form.phoneNumber ||
      !form.acceptedTerms ||
      !form.acceptedPrivacyPolicy
    );
  };

  const addContestant = async () => {
    if (isSavingRef.current) {
      return;
    }

    if (validateForm()) return;

    isSavingRef.current = true;
    setIsSaveButtonLoading(true);
    const resp = await contestService.addContestant({
      contestId: contest.contestId,
      name: form.name,
      email: form.email,
      phoneNumber: form.phoneNumber,
    });
    setIsSaveButtonLoading(false);
    isSavingRef.current = false;
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      onCustomerRegistered();
    }
  };

  const clearErrors = (field: keyof ContestForm) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  return (
    <>
      <div className="contest-content-wrapper">
        <h1>{contest.title}</h1>
        <p
          dangerouslySetInnerHTML={{ __html: contest.description ?? '' }}
          className="contest-description"
        />
        <p className="contest-period">{contest.period}</p>
        <div
          dangerouslySetInnerHTML={{ __html: contest.prizes ?? '' }}
          className="contest-awards"
        />
        <div className="contest-steps">
          <h2>{translate(resources.contests.stepsTitle)}</h2>
          <ul>
            <li>
              <img
                src={rewriteImageUrl(contest.step1Icon)}
                alt="Contest step 1"
              />
              <p dangerouslySetInnerHTML={{ __html: contest.step1 ?? '' }} />
            </li>
            <li className="contest-steps-separator"></li>
            <li>
              <img
                src={rewriteImageUrl(contest.step2Icon)}
                alt="Contest step 2"
              />
              <p dangerouslySetInnerHTML={{ __html: contest.step2 ?? '' }} />
            </li>
          </ul>
        </div>
      </div>
      <div className="contest-form" id="contest-form">
        <div className="contest-form-wrapper">
          <h2>{translate(resources.contests.form.title)}</h2>
          <Input
            label={translate(resources.contests.form.name)}
            value={form.name}
            onChange={e => onFormChange('name', e.value)}
            errors={errors.name}
            clearErrors={() => clearErrors('name')}
            mandatory
          />
          <Input
            label={translate(resources.contests.form.email)}
            value={form.email}
            onChange={e => onFormChange('email', e.value)}
            errors={errors.email}
            clearErrors={() => clearErrors('email')}
            mandatory
          />
          <Input
            label={translate(resources.contests.form.phoneNumber)}
            value={form.phoneNumber}
            onChange={e => onFormChange('phoneNumber', e.value)}
            errors={errors.phoneNumber}
            clearErrors={() => clearErrors('phoneNumber')}
            mandatory
          />
          <div className="contest-form-checkboxes">
            <Checkbox
              label={
                (
                  <label className="k-checkbox-label">
                    {translate(resources.contests.form.agreeWtih)}{' '}
                    <Button
                      onClick={event =>
                        openContestTerms(event, contest.contestId, translate)
                      }
                    >
                      {translate(resources.contests.termsAndConditions)}
                    </Button>
                    *
                  </label>
                ) as any
              }
              isChecked={form.acceptedTerms}
              onChange={e => onFormChange('acceptedTerms', e.value)}
              errors={errors.acceptedTerms}
              clearErrors={() => clearErrors('acceptedTerms')}
            />
            <Checkbox
              label={
                (
                  <label className="k-checkbox-label">
                    {translate(resources.contests.form.understood)}{' '}
                    <Link
                      to="https://benefit.edenred.ro/politica-de-prelucrare-a-datelor.pdf"
                      target="_blank"
                    >
                      {translate(resources.contests.privacyPolicy)}
                    </Link>
                    *
                  </label>
                ) as any
              }
              isChecked={form.acceptedPrivacyPolicy}
              onChange={e => onFormChange('acceptedPrivacyPolicy', e.value)}
              errors={errors.acceptedPrivacyPolicy}
              clearErrors={() => clearErrors('acceptedPrivacyPolicy')}
            />
          </div>
          <Button
            onClick={addContestant}
            loading={isSaveButtonLoading}
            className="btn btn-solid"
          >
            {translate(resources.contests.register)}
          </Button>
        </div>
      </div>
    </>
  );
};

{
  /* Registration complete */
}
const ContestCustomerRegistered = ({
  contest,
}: {
  contest: ContestDetailsModel;
}) => {
  const { t: translate } = useTranslation();
  const confettiRef1 = useRef<HTMLDivElement>(null);
  const confettiRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (confettiRef1.current) {
      party.confetti(confettiRef1.current, confettiOptions);
    }
    if (confettiRef2.current) {
      party.confetti(confettiRef2.current, confettiOptions);
    }
  }, []);

  let link = '/flexible-benefits';
  if (contest.categoryId && contest.topLevelCategoryType) {
    if (contest.topLevelCategoryType === TopLevelCategories.BenefitClub) {
      link = `/club-benefits/${contest.categoryId}`;
    }
    if (contest.topLevelCategoryType === TopLevelCategories.FlexibleBenefits) {
      link = `/flexible-benefits/${contest.categoryId}`;
    }
  }

  return (
    <div className="contest-content-wrapper contest-section registration-complete-section">
      <h1 className="confetti-wrapper">
        <span className="confetti-element" ref={confettiRef1}></span>
        <span className="confetti-element" ref={confettiRef2}></span>
        {translate(resources.contests.registerSuccesful)}
      </h1>
      <Link to={link} className="btn btn-solid">
        {translate(resources.contests.startShopping)}
      </Link>
    </div>
  );
};

{
  /* Pending winners */
}
const ContestPendingForWinners = ({
  contest,
}: {
  contest: ContestDetailsModel;
}) => {
  const { t: translate } = useTranslation();

  return (
    <div className="contest-content-wrapper contest-section pending-winners-section">
      <h1>{translate(resources.contests.contestOver)}</h1>
      <p>
        {translate(resources.contests.thankYouForParicipating)}
        <br />
        {translate(resources.contests.winnersPending)}
      </p>
      <Button
        onClick={event => openContestTerms(event, contest.contestId, translate)}
      >
        {translate(resources.contests.termsAndConditions)}
      </Button>
    </div>
  );
};

{
  /* Winners list */
}

const ContestWinners = ({ contest }: { contest: ContestDetailsModel }) => {
  const { t: translate } = useTranslation();
  const confettiRef1 = useRef<HTMLDivElement>(null);
  const confettiRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (confettiRef1.current) {
      party.confetti(confettiRef1.current, confettiOptions);
    }
    if (confettiRef2.current) {
      party.confetti(confettiRef2.current, confettiOptions);
    }
  }, []);

  return (
    <div className="contest-content-wrapper contest-section winners-list-section">
      <h1>{translate(resources.contests.contestOver)}</h1>
      <p>
        {translate(resources.contests.winnersCongratulations, {
          0: contest.title,
        })}
      </p>
      <div className="confetti-wrapper">
        <span className="confetti-element" ref={confettiRef1}></span>
        <span className="confetti-element" ref={confettiRef2}></span>
        <div
          dangerouslySetInnerHTML={{ __html: contest.winners ?? '' }}
          className="winners-list"
        />
      </div>

      <p>
        {translate(resources.contests.thankYouForParicipating)}
        <br />
        <Button
          onClick={event =>
            openContestTerms(event, contest.contestId, translate)
          }
        >
          {translate(resources.contests.termsAndConditions)}
        </Button>
      </p>
    </div>
  );
};

const getRequiredValidationError = (target: string): ValidationError => ({
  target,
  code: resources.validation.required,
  severity: 'error',
  placeholders: {},
});
